/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';

import React, { useEffect, useState } from 'react';

import { fireAnalyticsLink } from '../../../Utilities/analytics/clickTrack';
import {
    IProgressiveLeasingEstimatorData,
    ProgressiveLeasingEstimatorPayFrequency,
    ProgressiveLeasingEstimatorRequestType
} from '../mfrm-buybox.props.autogenerated';

export interface IMfrmProgressiveLeasingEstimatorProps {
    progressiveLeasingEstimator: IProgressiveLeasingEstimatorData;
    productPrice: number;
    ctx: Msdyn365.IRequestContext;
    isLeaseActive: boolean;
}

export const MfrmProgressiveLeasingEstimator: React.FC<IMfrmProgressiveLeasingEstimatorProps> = props => {
    const { infoText, estimatorLink } = props.progressiveLeasingEstimator;
    const [isOpen, setModalStatus] = useState(false);
    let estimatorUrl = buildEstimatorURL(props.progressiveLeasingEstimator, props.productPrice);
    //   If MSFT suggests us to use imageSettings, we will uncomment this piece of code (linked to line : 73)
    // const imageProps = {
    //     gridSettings: props.ctx.gridSettings || {},
    //     imageSettings: progressiveLogo && progressiveLogo.imageSettings,
    // };

    const _openModal = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!props.isLeaseActive) {
            e.preventDefault();
        } else {
            fireAnalyticsLink({
                eventName: 'internal navigation',
                eventCategory: 'internal links',
                eventAction: 'to: Progressive Leasing modal',
                eventLabel: `from PDP financing “${estimatorLink?.linkText}” link`,
                eventNoninteraction: 'false'
            });
            setModalStatus(true);
        }
    };

    const _closeModal = () => {
        setModalStatus(false);
    };

    useEffect(() => {
        const iframe = Msdyn365.msdyn365Commerce.isBrowser && document.querySelector('.msc-modal__iframe');
        if (iframe) {
            if (!isOpen) {
                (iframe as HTMLIFrameElement).src = 'about:blank';
            } else {
                (iframe as HTMLIFrameElement).src = estimatorUrl!;
            }
        }
    }, [isOpen]);

    useEffect(() => {
        estimatorUrl = buildEstimatorURL(props.progressiveLeasingEstimator, props.productPrice);
    }, [props.productPrice]);

    return (
         <>
        <div className='ms-buybox__lease-to-own redesign'>
            <div className='ms-buybox__lease-to-own__text'>or {infoText?.replace(/\u2028/, '')}
                <a className={'ms-buybox__lease-to-own__text-link'}
                    onClick={_openModal}
                    aria-label={estimatorLink?.ariaLabel}
                    role='link'
                    href='javascript:void(0)'
                >
                    <span className="ms-buybox__lease-to-own__estimator-link-text">{estimatorLink?.linkText || 'Link'}</span>
                    <span className="prevent-wrap">
                        <span aria-hidden="true"></span>
                    </span>
                </a>
            </div>
            <div className='msc-modal__content'>
                <Modal isOpen={isOpen} className='hangtag-modal' toggle={_closeModal}>
                    <ModalHeader className='msc-modal__progressive-header'>
                        {/* // TODO: Remove the hardcoding once MSFT fixes the issue with imageSettings */}
                        {/* <Msdyn365.Image {...progressiveLogo} {...imageProps} /> */}
                        <img
                            src='https://images-us-prod.cms.commerce.dynamics.com/cms/api/cncgmclkfv/imageFileData/MA8gue?pubver=0'
                            alt=' Progressive Logo'
                        />
                        {<button type='button' className='msc-modal__close-button' aria-label='Close' onClick={_closeModal} />}
                    </ModalHeader>
                    <ModalBody>
                        {
                            <iframe
                                className='msc-modal__iframe'
                                // tslint:disable-next-line: react-iframe-missing-sandbox
                                sandbox='allow-popups allow-scripts allow-same-origin'
                            />
                        }
                    </ModalBody>
                </Modal>
            </div>
        </div>
        </>
    );
};

const buildEstimatorURL = (progressiveLeasingEstimator: IProgressiveLeasingEstimatorData, itemCost: number) => {
    const { storeId, estimatorLink, requestType, payFrequency, approveMeName, approveMeSlugValue } = progressiveLeasingEstimator;

    // base url
    const estimatorURL =
        (estimatorLink &&
            estimatorLink.linkUrl &&
            estimatorLink.linkUrl.destinationUrl &&
            new URL(estimatorLink.linkUrl.destinationUrl?.replace('#', '%23'))) ||
        undefined;

    // add params
    const requestTypeValue = getRequestType(requestType);
    requestTypeValue && estimatorURL?.searchParams.append('requestType', requestTypeValue);

    const payFrequencyValue = getPayFrequency(payFrequency);
    payFrequencyValue && estimatorURL?.searchParams.append('payFrequency', payFrequencyValue);

    estimatorURL?.searchParams.append('storeId', storeId);
    estimatorURL?.searchParams.append('itemCost', itemCost.toString());

    approveMeName && estimatorURL?.searchParams.append('approveMeName', approveMeName);
    approveMeSlugValue && estimatorURL?.searchParams.append('approveMeSlugValue', approveMeSlugValue);

    const stateCode = getStateCode();
    stateCode && estimatorURL?.searchParams.append('stateCode', stateCode);

    return estimatorURL ? decodeURIComponent(estimatorURL.href) : undefined;
};

const getRequestType = (type: ProgressiveLeasingEstimatorRequestType) => {
    switch (type) {
        case ProgressiveLeasingEstimatorRequestType.pdp:
            return 'pdp';
        case ProgressiveLeasingEstimatorRequestType.checkoutPdp:
            return 'checkout-pdp';
        default:
            return undefined;
    }
};

const getPayFrequency = (frequency: ProgressiveLeasingEstimatorPayFrequency) => {
    switch (frequency) {
        case ProgressiveLeasingEstimatorPayFrequency.weekly:
            return 'weekly';
        case ProgressiveLeasingEstimatorPayFrequency.biWeekly:
            return 'biWeekly';
        case ProgressiveLeasingEstimatorPayFrequency.semiMonthly:
            return 'semiMonthly';
        case ProgressiveLeasingEstimatorPayFrequency.monthly:
            return 'monthly';
        default:
            return undefined;
    }
};

// TODO: will use MaxMind Geolocation to get state code in the future
const getStateCode = (): string | undefined => {
    const prefferedStoreData = localStorage.getItem('_mfrm__prefferedStore_');
    const parsedPrefferedStoreData = prefferedStoreData && JSON.parse(prefferedStoreData);

    return parsedPrefferedStoreData?.State;
};
